/**
 * ⚠️ CRITICAL STANDARDS COMPLIANCE ⚠️
 *
 * Uses Material Design 3 controls for the sign-in flow. If Material Web adds a
 * dedicated sign-in component in the future, replace this implementation and
 * remove the custom logic.
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { SignalWatcher } from '@lit-labs/signals';
import { firebaseAuthState, signIn } from '@df/state';
let DfSignIn = class DfSignIn extends SignalWatcher(LitElement) {
    constructor() {
        super();
        this.email = '';
        this.password = '';
    }
    static { this.styles = css `
    :host {
      display: block;
      font-family: var(--df-font-family, 'Roboto', sans-serif);
    }

    .surface {
      max-width: 420px;
      padding: 24px;
      border-radius: 16px;
      background: var(--md-sys-color-surface, #ffffff);
      border: 1px solid var(--md-sys-color-outline-variant, rgba(15, 23, 42, 0.12));
      box-shadow: 0 14px 32px rgba(15, 23, 42, 0.08);
    }

    h2 {
      margin: 0 0 20px;
      font-size: 1.6rem;
      font-weight: 600;
      color: var(--md-sys-color-on-surface, #0f172a);
    }

    form {
      display: flex;
      flex-direction: column;
      gap: 20px;
    }

    md-circular-progress {
      display: block;
      margin: 16px auto 0;
    }

    .error {
      padding: 12px;
      border-radius: 12px;
      background: var(--md-sys-color-error-container, rgba(186, 26, 26, 0.12));
      color: var(--md-sys-color-on-error-container, #410e0b);
      font-size: 0.9rem;
    }
  `; }
    render() {
        const authState = firebaseAuthState.get();
        const isLoading = authState.authState === 'loading';
        const error = authState.error;
        return html `
      <div class="surface">
        <h2>Sign In</h2>
        ${error ? html `<div class="error" role="alert">${error}</div>` : nothing}

        <form @submit=${this._handleSubmit}>
          <md-outlined-text-field
            type="email"
            label="Email"
            .value=${this.email}
            autocomplete="email"
            ?disabled=${isLoading}
            required
            @input=${this._handleEmailInput}
          ></md-outlined-text-field>

          <md-outlined-text-field
            type="password"
            label="Password"
            .value=${this.password}
            autocomplete="current-password"
            ?disabled=${isLoading}
            required
            @input=${this._handlePasswordInput}
          ></md-outlined-text-field>

          <md-filled-button type="submit" ?disabled=${isLoading}>
            ${isLoading ? 'Signing In…' : 'Sign In'}
          </md-filled-button>

          ${isLoading ? html `<md-circular-progress indeterminate></md-circular-progress>` : nothing}
        </form>
      </div>
    `;
    }
    _handleEmailInput(event) {
        this.email = event.target.value ?? '';
    }
    _handlePasswordInput(event) {
        this.password = event.target.value ?? '';
    }
    async _handleSubmit(event) {
        event.preventDefault();
        const credentials = {
            email: this.email,
            password: this.password,
        };
        try {
            await signIn(credentials);
            this.dispatchEvent(new CustomEvent('df-sign-in-success', {
                detail: { email: this.email },
                bubbles: true,
                composed: true,
            }));
            this.email = '';
            this.password = '';
        }
        catch (error) {
            this.dispatchEvent(new CustomEvent('df-sign-in-error', {
                detail: {
                    error: error instanceof Error ? error.message : 'Sign in failed',
                },
                bubbles: true,
                composed: true,
            }));
        }
    }
};
__decorate([
    state()
], DfSignIn.prototype, "email", void 0);
__decorate([
    state()
], DfSignIn.prototype, "password", void 0);
DfSignIn = __decorate([
    customElement('df-sign-in')
], DfSignIn);
export { DfSignIn };
//# sourceMappingURL=df-sign-in.js.map